// Report-only proctoring collector — aptitude-only, silent (no in-exam warnings).
//
// Runs MediaPipe FaceLandmarker in the browser on the existing hidden proctor
// <video>, derives behavioural signals (gaze, head turn, face count),
// runs a hysteresis state machine that emits time-bounded events, and batch-flushes
// them to POST students/assessments/proctoring/events every 30s (+ on flush()). No
// server compute — this is the free tier of the split-cost design.
//
// CDN-loaded via /* webpackIgnore: true */ dynamic import so webpack never tries to
// bundle @mediapipe/tasks-vision (the Assessment-React bundle must stay lean and must
// not gain a process.env reference — see memory re: blank-screen). If the CDN/binary
// fails to load, the collector silently no-ops and the existing snapshot cron still
// runs. Nothing here ever interrupts the candidate.
import { useCallback, useRef } from 'react'
import studentRequest from 'utils/studentRequest'

// ---- thresholds (policy, tunable) -----------------------------------------
const THRESH = {
  EYE_CONTACT_AWAY_MS: 2000, // gaze off-center sustained this long → eye_contact_lost
  GAZE_BELOW_MS: 3000, // looking down sustained this long → gaze_below_screen (a phone glance is 1–3s, not 10s)
  GAZE_OFFSIDE_MS: 3000, // horizontal gaze off sustained this long → gaze_off_side
  HEAD_TURN_MS: 3000, // head yaw sustained this long → head_turned
  NO_FACE_MS: 2000, // no face sustained this long → no_face
  HEAD_YAW_DEG: 35,
  HEAD_PITCH_DOWN_DEG: 12, // head tilted ≥12° below the calibrated baseline → looking down (phone)
  GAZE_VERT_HIGH: 0.62, // > this (looking down) counts as below-screen gaze
  GAZE_VERT_LOW: 0.38, // < this (looking up) counts as above-screen gaze — a second screen can sit above the webcam too
  GAZE_HORZ_LO: 0.35, // < this counts as looking left
  GAZE_HORZ_HI: 0.65, // > this counts as looking right
  EYE_CENTER_MAX_DEV: 0.22, // combined gaze deviation from centre before eye-contact lost
}

// ---- environment / window signals (Tier 1, DOM-only) -----------------------
// A web page cannot enumerate processes or windows — the browser sandbox
// forbids it. What it CAN see is the *effect* of another app being used: focus
// leaving while our document stays visible, our window occupying only part of
// the display, and the compositor no longer painting us. Those three cover the
// realistic same-device routes (split screen, alt-app, virtual-desktop switch,
// an opaque window parked over the exam) that gaze tracking is blind to,
// because the candidate's face never leaves the camera in any of them.
const ENV = {
  STARTUP_GRACE_MS: 1000, // ignore the focus churn around entering fullscreen / granting camera
  FOCUS_LOST_COOLDOWN_MS: 2000,
  // A maximised browser normally exceeds ~95% in both dimensions. These bounds
  // catch half/third snaps while tolerating taskbars, window borders and OS
  // display scaling.
  SPLIT_MIN_WIDTH_RATIO: 0.88,
  SPLIT_MIN_HEIGHT_RATIO: 0.85,
  SPLIT_MIN_AREA_RATIO: 0.78,
  SPLIT_COOLDOWN_MS: 15000,
  SPLIT_POLL_MS: 1000,
  SPLIT_RESIZE_DEBOUNCE_MS: 700,
  // rAF is driven by the compositor; setInterval is only throttled. Wall time
  // advancing while frames stop, with the document still claiming visible,
  // means the window isn't being painted. 2.5s ignores ordinary dropped frames
  // and a busy main thread (MediaPipe inference included).
  OCCLUSION_FRAME_GAP_MS: 2500,
  OCCLUSION_COOLDOWN_MS: 15000,
  OCCLUSION_POLL_MS: 500,
}

const FLUSH_INTERVAL_MS = 30000
const LANDMARKER_NUM_FACES = 2
const CALIB_DURATION_MS = 3000 // auto-baseline from the first 3s of a centered face
const CALIB_MIN_SAMPLES = 20

// ---- "reading from another device" fusion (per question) -------------------
// A second-device cheat has a signature: the candidate repeatedly glances
// off-plane (down at a phone / aside) to READ, then looks back to ANSWER. We
// score that per question and only flag when multiple independent signals agree
// — single signals stay informational (report-only: a human reviews flags).
const GLANCE = {
  MIN_DWELL_MS: 400, // ignore <0.4s flicks (blink/saccade noise)
  MAX_DWELL_MS: 8000, // >8s is a sustained look-away (its own event), not a read-glance
  CYCLES_SUSPECT: 2, // ≥2 read-glances in one question = signal A
  OFFSCREEN_RATIO_SUSPECT: 0.25, // off-plane >25% of question time = signal B
  ANSWER_AFTER_GLANCE_MS: 4000, // answer within 4s of a glance-back = read-then-transcribe (signal C)
}

// MediaPipe FaceLandmarker landmark indices used for gaze / EAR.
const LM = {
  L_IRIS: 468, R_IRIS: 473,
  L_OUTER: 33, L_INNER: 133, R_OUTER: 263, R_INNER: 362,
  L_UPPER: 159, L_LOWER: 145, R_UPPER: 386, R_LOWER: 374,
  L_OUTER_TOP: 159, L_OUTER_BOT: 145, // vertical extrema reused above
}

// ponytail: thresholds are heuristic — not a calibrated gaze tracker. Upgrade path:
// ship a per-candidate calibration baseline (Phase 5 BiometricCheck) and feed it in
// via `calibration`; this hook already consumes it when present.

// Native dynamic importer built at runtime so Babel/webpack never statically see
// the import() and can't downgrade it to require() (this repo's .babelrc emits
// CJS). Result: a real browser `import()` of the CDN ESM bundle. No npm dep, no
// bundle growth, no build change. Degrades to no-op on load failure.
const dynImport = new Function('spec', 'return import(spec)')
const TASKS_VISION_VERSION = '0.10.18'
const MP_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${TASKS_VISION_VERSION}`
const MP_JS = `${MP_BASE}/vision_bundle.mjs`
const MP_WASM = `${MP_BASE}/wasm`

// MediaPipe FaceLandmarker REQUIRES baseOptions.modelAssetPath (the
// face_landmarker.task model asset). Without it createFromOptions throws
// "Either modelAssetPath or modelAssetBuffer must be set" — and the old code
// swallowed that in .catch → landmarker=null → tick() no-op'd forever → zero
// client events → report showed a clean 100% eye-contact / high score even
// though the candidate wasn't watched. That silent no-op is exactly the bug
// this hook's header warns about; it hid a totally dead collector. The model
// is fetched from Google's model host (CORS-enabled) on first use, cached by
// the browser thereafter.
const FACE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task'

let _landmarkerPromise = null
let _lastLoadFailAt = 0
const LOAD_RETRY_COOLDOWN_MS = 15000 // don't hammer the CDN if creation keeps failing

async function createLandmarker(delegate) {
  const vision = await dynImport(MP_JS)
  const { FaceLandmarker, FilesetResolver } = vision
  const fileset = await FilesetResolver.forVisionTasks(MP_WASM)
  return FaceLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: FACE_MODEL_URL, delegate },
    runningMode: 'VIDEO',
    numFaces: LANDMARKER_NUM_FACES,
    outputFaceBlendshapes: true,
    outputFacialTransformationMatrixes: true,
  })
}

async function getLandmarker() {
  if (_landmarkerPromise) return _landmarkerPromise
  if (_lastLoadFailAt && Date.now() - _lastLoadFailAt < LOAD_RETRY_COOLDOWN_MS) return null
  _landmarkerPromise = (async () => {
    try {
      // GPU is far faster for VIDEO-mode 30fps; fall back to CPU if the device's
      // GPU delegate is unavailable (some integrated GPUs / WebGL contexts reject it).
      return await createLandmarker('GPU')
    } catch (gpuErr) {
      console.warn('[PROCTORING_COLLECTOR] GPU delegate failed, retrying CPU:', gpuErr?.message || gpuErr)
      try {
        return await createLandmarker('CPU')
      } catch (cpuErr) {
        console.error('[PROCTORING_COLLECTOR] FaceLandmarker DISABLED (eye-contact metrics will be missing):', cpuErr?.message || cpuErr)
        _lastLoadFailAt = Date.now()
        return null
      }
    }
  })()
  const resolved = await _landmarkerPromise
  if (resolved) console.log('[PROCTORING_COLLECTOR] FaceLandmarker ready — collecting gaze/eye-contact events')
  return resolved
}

function clamp01(v) { return Math.max(0, Math.min(1, v)) }

// Horizontal gaze ratio (0 = looking left, 1 = looking right, 0.5 = centre).
function gazeHoriz(lm) {
  const lx = lm[LM.L_OUTER]?.x ?? 0
  const rx = lm[LM.L_INNER]?.x ?? 0
  const iris = lm[LM.L_IRIS]?.x ?? (lx + rx) / 2
  if (rx === lx) return 0.5
  return clamp01((iris - lx) / (rx - lx))
}

// Vertical gaze ratio (0 = looking up, 1 = looking down, 0.5 = centre).
function gazeVert(lm) {
  const top = lm[LM.L_UPPER]?.y ?? 0
  const bot = lm[LM.L_LOWER]?.y ?? 0
  const iris = lm[LM.L_IRIS]?.y ?? (top + bot) / 2
  if (bot === top) return 0.5
  return clamp01((iris - top) / (bot - top))
}


// Head yaw in degrees from the 4x4 facial transformation matrix (row-major).
function headYawDeg(matrix) {
  if (!matrix || matrix.length < 16) return 0
  const m = matrix.data ? matrix.data : matrix
  // z-axis rotation yaw; m[8]=sin(yaw)·... use simple atan2 of basis vectors.
  const yaw = Math.atan2(m[8], m[10])
  return (yaw * 180) / Math.PI
}

// Head PITCH in degrees from the same matrix. Positive ≈ looking DOWN (chin to
// chest), which is the dominant motion when a candidate glances at a phone in
// their lap or on the desk — iris-vertical alone barely moves in that pose, so
// pitch is the signal that actually catches "looking at my phone". Sign
// convention varies across MediaPipe builds, so callers compare against a
// per-candidate calibrated baseline rather than an absolute zero.
function headPitchDeg(matrix) {
  if (!matrix || matrix.length < 16) return 0
  const m = matrix.data ? matrix.data : matrix
  // rotation about the x-axis: -R[2][1] = -m[9], normalised by the remaining basis.
  const pitch = Math.atan2(-m[9], Math.sqrt(m[8] * m[8] + m[10] * m[10]))
  return (pitch * 180) / Math.PI
}

export default function useProctoringCollector() {
  const rafRef = useRef(0)
  const videoElRef = useRef(null)
  const startedAtRef = useRef(0)
  const bufferRef = useRef([])
  const flushTimerRef = useRef(null)
  const metaRef = useRef({ assessmentAssignedId: null, studentId: null, sessionId: null })
  const onSuspicionRef = useRef(null) // host callback to densify server snapshots
  const calibRef = useRef(null) // { vertBaseline, pitchBaseline }
  const calibSamplesRef = useRef([]) // auto-baseline accumulator: [{ gv, pitch }]
  const calibDoneRef = useRef(false)
  // Calibration is anchored to the first frame with a detected face, NOT the
  // session clock — the MediaPipe model loads from CDN seconds after start(), so
  // by the first real frame the session clock has already passed 3s and the old
  // code locked in an empty "weak" baseline. 0 = not yet started.
  const calibStartedAtRef = useRef(0)

  // Per-question accumulator for the read-from-device fusion. Reset on each
  // setActiveQuestion(). offGazeSince tracks an in-flight off-plane glance.
  const qRef = useRef({
    questionId: null, index: null, shownAt: 0,
    glanceCycles: 0, offScreenMs: 0, offGazeSince: 0,
    lastGlanceBackAt: 0, answered: false,
  })

  // Live state-machine trackers held across frames.
  const stRef = useRef({
    awaySince: 0, belowSince: 0, offSideSince: 0, turnSince: 0, noFaceSince: 0,
    emitting: {}, // type -> { startMs } for an open (ongoing) event
  })

  // Environment/window watchers (split screen, focus loss, occlusion). Held
  // separately from stRef because they're DOM-driven, not frame-driven: they
  // must keep working even when MediaPipe never loads (CDN blocked) or the
  // camera is denied, which is precisely when the gaze signals are absent and
  // an environment tell is the only thing left watching the candidate.
  const envRef = useRef({
    startedAt: 0, lastRafAt: 0, raf: 0, watchdog: null, poll: null,
    resizeTimer: null, lastEmitAt: {}, cleanup: null,
  })

  // Aptitude/Communication/Role_Based lean heavily on typed/written answers,
  // where looking at the keyboard or scratch paper is normal candidate
  // behaviour — so "looked away" / "head turned" / "gaze below screen" is
  // constant false-positive noise for those assessment types, not a signal.
  // Set once via start({ disableGazeEvents }); doesn't affect the read-glance
  // ("reading detected") fusion below, which stays on everywhere.
  const gazeEventsDisabledRef = useRef(false)

  const sessionMs = useCallback(() => (startedAtRef.current ? Date.now() - startedAtRef.current : 0), [])

  const flush = useCallback(async () => {
    const events = bufferRef.current.splice(0)
    if (!events.length) return
    const { assessmentAssignedId, studentId, sessionId } = metaRef.current
    if (!assessmentAssignedId) return
    try {
      await studentRequest.post('students/assessments/proctoring/events', {
        assessmentAssignedId, studentId, sessionId, events,
      })
    } catch (e) {
      // Network/blip — put the events back so the next flush retries (idempotent
      // server dedups, so a duplicate on success-after-failure is safe).
      bufferRef.current.unshift(...events)
      console.warn('[PROCTORING_COLLECTOR] flush failed (will retry):', e?.message || e)
    }
  }, [])

  const emit = useCallback((type, startMs, durationMs, severity, confidence, evidenceObjectKey, meta) => {
    bufferRef.current.push({
      type, source: 'client', startMs,
      endMs: durationMs ? startMs + durationMs : startMs,
      durationMs: durationMs || 0,
      severity, confidence, evidenceObjectKey: evidenceObjectKey || null,
      meta: meta || {},
    })
  }, [])

  // Public: record a discrete interaction event from the host page (tab hidden,
  // fullscreen exit, second display). These are DOM-derived signals the page
  // already detects for its in-exam warnings; we tee them into the same
  // report-only pipeline instead of adding duplicate listeners. No-op until the
  // collector has started (no assessmentAssignedId / clock).
  const recordEvent = useCallback((type, { severity = 'medium', confidence = 0.95, durationMs = 0, meta = {} } = {}) => {
    if (!startedAtRef.current || !metaRef.current.assessmentAssignedId) return
    emit(type, sessionMs(), durationMs, severity, confidence, null, meta)
    if (severity === 'high' && onSuspicionRef.current) onSuspicionRef.current(type)
  }, [emit, sessionMs])

  // Attach the DOM environment watchers. One implementation here covers every
  // proctored assessment type, since all four host pages drive this collector.
  // Detached in stop(); safe to call twice (detach first).
  const attachEnvironmentWatchers = useCallback(() => {
    const env = envRef.current
    if (env.cleanup) env.cleanup()
    env.startedAt = performance.now()
    env.lastRafAt = performance.now()
    env.lastEmitAt = {}

    const emitEnv = (type, severity, meta, cooldownMs) => {
      const t = performance.now()
      if (!startedAtRef.current) return
      if (t - (env.lastEmitAt[type] || -Infinity) < cooldownMs) return
      env.lastEmitAt[type] = t
      recordEvent(type, { severity, confidence: 0.9, meta })
    }

    // Focus leaving a document that is still visible means focus went somewhere
    // outside this tab — another application, the other half of a split screen,
    // or a focusable overlay. Browser APIs cannot name that window, only that
    // one exists. (A plain tab switch hides the document, so the host page's
    // existing tab_hidden fires instead and this stays quiet.)
    const onBlur = () => {
      if (document.hidden) return
      if (performance.now() - env.startedAt < ENV.STARTUP_GRACE_MS) return
      emitEnv('window_focus_lost', 'medium', {}, ENV.FOCUS_LOST_COOLDOWN_MS)
    }

    // Compare the browser's outer window against the usable display. Poll as
    // well as listen for resize: Windows can snap a window before the session
    // starts, and display scaling sometimes coalesces or suppresses the resize
    // event entirely. Skipped while genuinely fullscreen — which is the normal
    // state of an exam, so this only speaks after a fullscreen exit.
    const checkWindowShare = () => {
      if (!startedAtRef.current || document.fullscreenElement || !window.screen) return
      const availW = Number(window.screen.availWidth) || 0
      const availH = Number(window.screen.availHeight) || 0
      const outerW = Number(window.outerWidth) || 0
      const outerH = Number(window.outerHeight) || 0
      // Bail on any missing dimension rather than treating it as zero: some
      // embedded/automation contexts report outerWidth 0, which would divide
      // out to a 0% window share and accuse an honest candidate of splitting
      // their screen.
      if (!availW || !availH || !outerW || !outerH) return
      const widthRatio = Math.min(1, outerW / availW)
      const heightRatio = Math.min(1, outerH / availH)
      const areaRatio = widthRatio * heightRatio
      if (
        widthRatio < ENV.SPLIT_MIN_WIDTH_RATIO
        || heightRatio < ENV.SPLIT_MIN_HEIGHT_RATIO
        || areaRatio < ENV.SPLIT_MIN_AREA_RATIO
      ) {
        emitEnv('split_screen', 'high', {
          widthPct: Math.round(widthRatio * 100),
          heightPct: Math.round(heightRatio * 100),
          areaPct: Math.round(areaRatio * 100),
          screenX: Math.round(window.screenX || 0),
          screenY: Math.round(window.screenY || 0),
        }, ENV.SPLIT_COOLDOWN_MS)
      }
    }
    const onResize = () => {
      clearTimeout(env.resizeTimer)
      env.resizeTimer = setTimeout(checkWindowShare, ENV.SPLIT_RESIZE_DEBOUNCE_MS)
    }

    // Dedicated rAF heartbeat — deliberately NOT the tick() loop, which awaits
    // getLandmarker() and would stall the heartbeat on a slow CDN fetch and
    // report that stall as an occlusion.
    const frame = (t) => { env.lastRafAt = t; env.raf = requestAnimationFrame(frame) }
    env.raf = requestAnimationFrame(frame)
    env.watchdog = setInterval(() => {
      if (document.hidden) return
      const gapMs = performance.now() - env.lastRafAt
      if (gapMs >= ENV.OCCLUSION_FRAME_GAP_MS) {
        emitEnv('window_occluded', 'high', { frameGapMs: Math.round(gapMs) }, ENV.OCCLUSION_COOLDOWN_MS)
      }
    }, ENV.OCCLUSION_POLL_MS)
    env.poll = setInterval(checkWindowShare, ENV.SPLIT_POLL_MS)

    // A hidden tab also stops rAF. Reset the heartbeat on return so a normal
    // tab switch (already recorded as tab_hidden by the host page) isn't
    // double-counted as an occlusion.
    const onVisibility = () => { if (!document.hidden) env.lastRafAt = performance.now() }

    window.addEventListener('blur', onBlur)
    window.addEventListener('resize', onResize)
    document.addEventListener('visibilitychange', onVisibility)

    env.cleanup = () => {
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('resize', onResize)
      document.removeEventListener('visibilitychange', onVisibility)
      clearTimeout(env.resizeTimer)
      clearInterval(env.watchdog)
      clearInterval(env.poll)
      cancelAnimationFrame(env.raf)
      env.resizeTimer = null
      env.watchdog = null
      env.poll = null
      env.raf = 0
      env.cleanup = null
    }

    // Catch a window that was already snapped before the session began.
    setTimeout(checkWindowShare, 1200)
  }, [recordEvent])

  const detachEnvironmentWatchers = useCallback(() => {
    if (envRef.current.cleanup) envRef.current.cleanup()
  }, [])

  const closeIfOpen = (type) => {
    const open = stRef.current.emitting[type]
    if (!open) return
    const dur = sessionMs() - open.startMs
    const sev = severityFor(type, dur)
    emit(type, open.startMs, dur, sev, 0.85, null)
    delete stRef.current.emitting[type]
  }

  const openIfNot = (type) => {
    if (!stRef.current.emitting[type]) {
      stRef.current.emitting[type] = { startMs: sessionMs() }
    }
  }

  // Medium requires a sustained ~4s (post-trigger) deviation, not a brief glance —
  // short blips report as 'low' (and the PDF hides 'low' entirely) so a normal
  // 1-2s look-away isn't flagged as a concern.
  const MEDIUM_DUR_MS = 4000
  function severityFor(type, durMs) {
    if (type === 'multi_face' || type === 'phone_detected') return 'high'
    if (type === 'gaze_below_screen') return durMs >= 10000 ? 'high' : durMs >= MEDIUM_DUR_MS ? 'medium' : 'low'
    if (type === 'no_face') return durMs >= 8000 ? 'high' : durMs >= MEDIUM_DUR_MS ? 'medium' : 'low'
    return durMs >= MEDIUM_DUR_MS ? 'medium' : 'low'
  }

  const tick = useCallback(async () => {
    const landmarker = await getLandmarker()
    const video = videoElRef.current
    if (!landmarker || !video || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(tick)
      return
    }
    const now = performance.now()
    let faces = []
    let blend = null
    let matrix = null
    try {
      const res = landmarker.detectForVideo(video, now)
      faces = res.faceLandmarks || []
      blend = res.faceBlendshapes?.[0]?.categories || null
      matrix = res.facialTransformationMatrixes?.[0] || null
    } catch (e) {
      // transient decode error — skip this frame, keep looping
    }

    const st = stRef.current
    const nowMs = sessionMs()

    // ---- face count: no_face only on the client -------------------------
    // We deliberately do NOT emit a client-side multi_face. MediaPipe's
    // per-frame face count is too noisy (occasional sub-50ms double-hits from
    // glasses / glasses frame / re-detect) and floods the report with false
    // positives (60+ spurious "multiple faces" incidents per session, while the
    // actual images show none). Multi-face is a server-CV signal: the snapshot
    // cron runs YOLO on a real frame (see ProctoringReport.js _serverEventRow)
    // and emits one row per offending snapshot — that is the only source we
    // trust for "multiple faces". The client still detects single-face state
    // (no_face) for "candidate left the frame".
    if (faces.length === 0) {
      if (!st.noFaceSince) st.noFaceSince = nowMs
      else if (nowMs - st.noFaceSince >= THRESH.NO_FACE_MS) openIfNot('no_face')
    } else {
      closeIfOpen('no_face')
      st.noFaceSince = 0
    }

    if (faces.length === 1) {
      const lm = faces[0]
      const gh = gazeHoriz(lm)
      const gvRaw = gazeVert(lm)
      const pitchRaw = headPitchDeg(matrix)

      // ---- auto-calibration: average vertical gaze + head pitch from a centered
      // face for the first 3s OF DETECTED-FACE TIME (anchored to the first face
      // frame, not the session clock — the model loads from CDN seconds after
      // start()). Until calibration completes we don't open gaze/head events
      // (face-count events still fire — they don't need a baseline).
      if (!calibDoneRef.current) {
        if (!calibStartedAtRef.current) calibStartedAtRef.current = nowMs
        const devPre = Math.abs(gh - 0.5) + Math.abs(gvRaw - 0.5)
        if (devPre < 0.18) calibSamplesRef.current.push({ gv: gvRaw, pitch: pitchRaw })
        if (nowMs - calibStartedAtRef.current >= CALIB_DURATION_MS) {
          const s = calibSamplesRef.current
          const vertBaseline = s.length ? s.reduce((a, b) => a + b.gv, 0) / s.length : 0.5
          const pitchBaseline = s.length ? s.reduce((a, b) => a + b.pitch, 0) / s.length : 0
          calibRef.current = { vertBaseline, pitchBaseline }
          calibDoneRef.current = true
          const quality = s.length >= CALIB_MIN_SAMPLES ? 'ok' : 'weak'
          emit('calibration', 0, 0, quality === 'ok' ? 'low' : 'medium', 0.9, null)
          bufferRef.current[bufferRef.current.length - 1].meta = { calibrationQuality: quality, samples: s.length }
        }
      }

      const baseline = calibRef.current?.vertBaseline ?? 0.5
      const pitchBaseline = calibRef.current?.pitchBaseline ?? 0
      const gv = gvRaw - (baseline - 0.5) // normalise iris-vertical against calibration centre
      const pitchDown = pitchRaw - pitchBaseline // +ve = head tilted further down than baseline
      const yaw = headYawDeg(matrix)
      const calibrated = calibDoneRef.current
      // "Looking down" = either the eyes drop OR (more reliably for a phone in
      // the lap) the head tilts down past the calibrated pitch baseline.
      const lookingDown = gv > THRESH.GAZE_VERT_HIGH || pitchDown >= THRESH.HEAD_PITCH_DOWN_DEG

      // ---- gaze + head events (armed only after calibration completes) ----
      if (calibrated) {
        if (!gazeEventsDisabledRef.current) {
          // gaze below screen (reading under the screen / at a phone)
          if (lookingDown) {
            if (!st.belowSince) st.belowSince = nowMs
            else if (nowMs - st.belowSince >= THRESH.GAZE_BELOW_MS) openIfNot('gaze_below_screen')
          } else {
            st.belowSince = 0
            closeIfOpen('gaze_below_screen')
          }

          // gaze off side
          const offSide = gh < THRESH.GAZE_HORZ_LO || gh > THRESH.GAZE_HORZ_HI
          if (offSide) {
            if (!st.offSideSince) st.offSideSince = nowMs
            else if (nowMs - st.offSideSince >= THRESH.GAZE_OFFSIDE_MS) openIfNot('gaze_off_side')
          } else {
            st.offSideSince = 0
            closeIfOpen('gaze_off_side')
          }

          // eye contact lost (combined gaze deviation OR head turned/tilted away)
          const dev = Math.sqrt((gh - 0.5) ** 2 + (gv - 0.5) ** 2)
          const away = dev > THRESH.EYE_CENTER_MAX_DEV || Math.abs(yaw) > 25 || lookingDown
          if (away) {
            if (!st.awaySince) st.awaySince = nowMs
            else if (nowMs - st.awaySince >= THRESH.EYE_CONTACT_AWAY_MS) openIfNot('eye_contact_lost')
          } else {
            st.awaySince = 0
            closeIfOpen('eye_contact_lost')
          }
        }

        // ---- per-question read-glance tracking (fusion signal A + B) --------
        // "Off-plane" = looking away from the screen in ANY direction — down,
        // up, or to either side — since a second device (phone, monitor, propped
        // notes) can sit anywhere around the webcam, not just below it. We count
        // discrete glance cycles and the off-plane dwell, scoped to the active
        // question (qRef is reset on setActiveQuestion). Cheap counters; the
        // verdict is computed at answer time / question change, never per frame.
        const q = qRef.current
        if (q.questionId) {
          const lookingUp = gv < THRESH.GAZE_VERT_LOW
          const offPlane = lookingDown || lookingUp || gh < THRESH.GAZE_HORZ_LO || gh > THRESH.GAZE_HORZ_HI
          if (offPlane) {
            if (!q.offGazeSince) q.offGazeSince = nowMs
          } else if (q.offGazeSince) {
            const dwell = nowMs - q.offGazeSince
            q.offGazeSince = 0
            // a real read-glance is long enough to read but not a sustained stare
            if (dwell >= GLANCE.MIN_DWELL_MS && dwell <= GLANCE.MAX_DWELL_MS) {
              q.glanceCycles += 1
              q.offScreenMs += dwell
              q.lastGlanceBackAt = nowMs // moment eyes returned to screen
            } else if (dwell > GLANCE.MAX_DWELL_MS) {
              q.offScreenMs += dwell // still counts toward dwell ratio
            }
          }
        }

        // head turned
        if (!gazeEventsDisabledRef.current) {
          if (Math.abs(yaw) > THRESH.HEAD_YAW_DEG) {
            if (!st.turnSince) st.turnSince = nowMs
            else if (nowMs - st.turnSince >= THRESH.HEAD_TURN_MS) openIfNot('head_turned')
          } else {
            st.turnSince = 0
            closeIfOpen('head_turned')
          }
        }
      }

    }

    rafRef.current = requestAnimationFrame(tick)
  }, [])

  // Close out the in-flight question: fuse its accumulated signals and, if a
  // read-from-device pattern is present, emit one external_reference_suspected
  // event scoped to that question. answeredFast = answer landed within
  // ANSWER_AFTER_GLANCE_MS of an off-plane glance-back (read-then-transcribe).
  const finalizeQuestion = useCallback(({ answeredFast = false, readingSuspected = false } = {}) => {
    const q = qRef.current
    if (!q.questionId || !calibDoneRef.current) return
    const elapsed = Math.max(1, sessionMs() - q.shownAt)
    // fold any still-open glance into the dwell total
    if (q.offGazeSince) {
      q.offScreenMs += sessionMs() - q.offGazeSince
      q.offGazeSince = 0
    }
    const offScreenRatio = q.offScreenMs / elapsed
    const sigA = q.glanceCycles >= GLANCE.CYCLES_SUSPECT // repeated read-glances
    const sigB = offScreenRatio >= GLANCE.OFFSCREEN_RATIO_SUSPECT // sustained off-screen
    const sigC = Boolean(answeredFast) // answer right after a glance-back
    const sigD = Boolean(readingSuspected) // audio read-aloud pace (interview only)
    const agreeing = [sigA, sigB, sigC, sigD].filter(Boolean).length
    if (agreeing >= 2) {
      // confidence scales with how many independent signals agree
      const confidence = Math.min(0.95, 0.45 + 0.15 * agreeing)
      emit('external_reference_suspected', q.shownAt, elapsed, agreeing >= 3 ? 'high' : 'medium', confidence, null, {
        questionId: q.questionId,
        questionIndex: q.index,
        glanceCycles: q.glanceCycles,
        offScreenRatio: Math.round(offScreenRatio * 100) / 100,
        readThenAnswer: sigC,
        readAloudPace: sigD,
      })
      // densify server snapshots to try to catch the second device on camera
      if (onSuspicionRef.current) onSuspicionRef.current('external_reference_suspected')
    }
  }, [emit, sessionMs])

  // Public: the host page calls this whenever the visible question changes.
  // Finalizes the previous question's verdict, then resets the accumulator.
  const setActiveQuestion = useCallback((index, questionId) => {
    if (!startedAtRef.current) return
    if (qRef.current.questionId && qRef.current.questionId !== questionId) {
      finalizeQuestion({ answeredFast: false })
    }
    qRef.current = {
      questionId: questionId || null, index: index ?? null, shownAt: sessionMs(),
      glanceCycles: 0, offScreenMs: 0, offGazeSince: 0, lastGlanceBackAt: 0, answered: false,
    }
  }, [finalizeQuestion, sessionMs])

  // Public: the host page calls this when an answer is selected for a question.
  // If the answer lands right after a glance-back, that's the read-then-answer
  // signal (C). We finalize immediately so the verdict is attributed correctly
  // even if the candidate lingers before navigating.
  const recordAnswer = useCallback((questionId, { readingSuspected = false } = {}) => {
    const q = qRef.current
    if (!startedAtRef.current || !q.questionId || q.questionId !== questionId) return
    if (q.answered) return // first selection is the telling one
    q.answered = true
    const sinceGlanceBack = q.lastGlanceBackAt ? sessionMs() - q.lastGlanceBackAt : Infinity
    finalizeQuestion({
      answeredFast: sinceGlanceBack <= GLANCE.ANSWER_AFTER_GLANCE_MS,
      readingSuspected,
    })
    // keep accumulating for the rest of the dwell, but don't double-flag:
    // reset counters so a later question-change finalize starts clean.
    q.glanceCycles = 0
    q.offScreenMs = 0
    q.lastGlanceBackAt = 0
  }, [finalizeQuestion, sessionMs])

  const start = useCallback(({ videoEl, assessmentAssignedId, studentId, sessionId, calibration, onSuspicion, disableGazeEvents }) => {
    if (!assessmentAssignedId) return
    videoElRef.current = videoEl
    metaRef.current = { assessmentAssignedId, studentId: studentId || null, sessionId: sessionId || null }
    onSuspicionRef.current = typeof onSuspicion === 'function' ? onSuspicion : null
    gazeEventsDisabledRef.current = Boolean(disableGazeEvents)
    if (startedAtRef.current) return // already running
    startedAtRef.current = Date.now()
    stRef.current = {
      awaySince: 0, belowSince: 0, offSideSince: 0, turnSince: 0, noFaceSince: 0,
      emitting: {},
    }
    calibSamplesRef.current = []
    calibDoneRef.current = false
    calibStartedAtRef.current = 0
    calibRef.current = calibration || null
    qRef.current = {
      questionId: null, index: null, shownAt: 0,
      glanceCycles: 0, offScreenMs: 0, offGazeSince: 0, lastGlanceBackAt: 0, answered: false,
    }
    // Fire-and-forget load so it's warm by first frame; tick no-ops until ready.
    getLandmarker()
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(tick)
    if (flushTimerRef.current) clearInterval(flushTimerRef.current)
    flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS)
    attachEnvironmentWatchers()
  }, [tick, flush, attachEnvironmentWatchers])

  const stop = useCallback(async () => {
    cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    detachEnvironmentWatchers()
    // finalize the last question's read-glance verdict before we tear down
    finalizeQuestion({ answeredFast: false })
    // close any events still open at submit time
    Object.keys(stRef.current.emitting).forEach(closeIfOpen)
    await flush()
    videoElRef.current = null
  }, [flush, finalizeQuestion, detachEnvironmentWatchers])

  // Record a calibration baseline (called from Phase 5 after the 3-sec step).
  const setCalibration = useCallback((vertBaseline) => {
    calibRef.current = { vertBaseline }
  }, [])

  return { start, stop, flush, setCalibration, recordEvent, setActiveQuestion, recordAnswer }
}